import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * O painel que se pede de novo sozinho (onda 318, spec 06b §5.3, M3).
 *
 * Um painel aberto na parede da sala, ou esquecido numa aba desde a
 * manhã, mostra o dia de horas atrás como se fosse o de agora. Por isso
 * cada metade do painel (o Hoje e o Andamento) é pedida de novo:
 *
 *  · a cada 5 minutos, enquanto a aba está à vista — com a aba escondida
 *    não se pede nada (ninguém está olhando, e o celular agradece);
 *  · ao voltar para a aba (`visibilitychange` para visível), que é quando
 *    alguém volta a olhar.
 *
 * A cabeça diz «atualizado às HH:MM» com o `atualizadoEm` da última
 * resposta boa. Uma atualização que falha não apaga a tela: os números de
 * antes ficam, com a hora de antes — que é o aviso de que estão velhos.
 *
 * `buscar` novo (outro período no Andamento) é outra pergunta: os dados da
 * pergunta de antes saem e a nova é feita na hora. Uma resposta que chega
 * depois de outra pergunta, ou depois de a tela fechar, é descartada.
 *
 * Entre as respostas da mesma pergunta, vale a mais nova que CHEGAR: uma
 * mais velha do que a que já está na tela é descartada (a ordem de chegada
 * não volta o painel no tempo), mas a resposta não precisa ser a do último
 * pedido. Exigir a do último deixava o painel em branco para sempre quando
 * cada resposta demorava mais do que o intervalo — o pedido seguinte saía
 * antes de ela chegar, e toda resposta já chegava «velha» (achado da suíte
 * inteira de 09/10: o 305.29, com o relógio adiantado e o banco cheio).
 *
 * A peça não sabe de onde vêm os dados (o molde não conhece a API): quem
 * monta o painel passa a função que busca.
 */

/** Cinco minutos, em milissegundos (spec 06b §5.3). */
export const CINCO_MINUTOS = 300_000;

export function usePainelVivo<T>(buscar: () => Promise<T>, intervaloMs = CINCO_MINUTOS): {
  dados: T | null;
  erro: unknown;
  atualizadoEm: Date | null;
  recarregar(): void;
} {
  const [dados, setDados] = useState<T | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const atual = useRef(buscar);
  /* Cada pedido leva um número (`vez`, o do último pedido feito). */
  const vez = useRef(0);
  /* Pedidos com número até este não valem: são de outra pergunta (outro
     `buscar`) ou de antes de a tela fechar. */
  const desde = useRef(0);
  /* O número da resposta que está na tela: uma mais velha não entra. */
  const mostrada = useRef(0);

  const recarregar = useCallback(() => {
    const minha = ++vez.current;
    let pedido: Promise<T>;
    try {
      pedido = atual.current();
    } catch (e) {
      setErro(e);
      return;
    }
    const vale = () => minha > desde.current && minha > mostrada.current;
    pedido.then(
      (r) => {
        if (!vale()) return;
        mostrada.current = minha;
        setDados(r);
        setErro(null);
        setAtualizadoEm(new Date());
      },
      (e: unknown) => {
        if (!vale()) return;
        mostrada.current = minha;
        setErro(e);
      },
    );
  }, []);

  useEffect(() => {
    atual.current = buscar;
    desde.current = vez.current;
    setDados(null);
    setErro(null);
    setAtualizadoEm(null);
    recarregar();

    const relogio = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') recarregar();
    }, intervaloMs);
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') recarregar();
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      window.clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoVoltar);
      desde.current = vez.current;
    };
  }, [buscar, intervaloMs, recarregar]);

  return { dados, erro, atualizadoEm, recarregar };
}
